import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Box, Container, Flex, Button } from '@radix-ui/themes'
import { ArrowLeftIcon } from 'lucide-react'

import useEstimateFetcher from '../hooks/useEstimateFetcher'
import EstimateDet from '../interfaces/EstimateDet'
import useEstimateVoid from '../hooks/useEstimateVoid'
import useEstimateExtend from '../hooks/useEstimateExtend'
import { getDateFromDb } from '../utils/utils'
import { AppPath } from '../enums/AppPath'
import { useFeatureFlag } from '../hooks/useFeatureFlag'

import DocFooter from './DocFooter'
import DocAnula from './DocAnula'
import InputField from './InputField'
import { ButtonAction, ButtonMenuSet, ButtonPrint } from './Buttons'
import EstimateDetail from './EstimateDetail'
import { PrintableCard } from './PrintableCard'

const EstimateDetailPage: React.FC<{ estimateId?: string; onClose?: () => void }> = ({ estimateId, onClose }) => {
  const { useNewDocumentUI } = useFeatureFlag()
  const [estimate, setEstimate] = useState<EstimateDet>()
  const [localError, setLocalError] = useState<string | null>(null)
  const { errorEstimate, fetchEstimate } = useEstimateFetcher()
  const [clickedIndex, setClickedIndex] = useState<number>(-1)
  const [motivo, setMotivo] = useState<string>('')
  const [expiresAt, setExpiresAt] = useState<string>('')
  const { errorVoidEstimate, voidEstimate } = useEstimateVoid()
  const { errorExtendEstimate, extendEstimate } = useEstimateExtend()
  const [isAnulada, setIsAnulada] = useState<boolean>(false)
  const [refreshPage, setRefreshPage] = useState<boolean>(false)
  const [dateModified, setDateModified] = useState<boolean>(false)
  // Extracting id parameter from the route
  const [idx, setIdx] = useState<string>('')
  const { id } = useParams<{ id: string }>()

  // Navigation and URL parameters
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const fromDeal = searchParams.get('fromDeal')

  useEffect(() => {
    const idAux = estimateId ?? id ?? ''
    setIdx(idAux)
    fetchDataAsync(idAux)
  }, [])

  useEffect(() => {
    if (refreshPage) fetchDataAsync(idx)
    setRefreshPage(false)
  }, [refreshPage])

  const fetchDataAsync = async (idAux: string) => {
    if (!idAux) {
      setLocalError('ID is not defined.')

      return // Exit early if `id` is undefined
    }
    const estimate = await fetchEstimate(idAux)

    if (!estimate) return
    setEstimate(estimate)

    const expDate = getDateFromDb(estimate?.expiresAt)
    setExpiresAt(expDate)
    setIsAnulada(!!estimate?.voidedAt)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'motivo') setMotivo(value)

    if (name === 'expiresAt') {
      setExpiresAt(value)
      setDateModified(true)
    }
  }

  const handleAnularClick = async () => {
    await voidEstimate(idx ?? '', motivo)

    // refrescamos la pantalla
    if (!errorVoidEstimate) {
      setRefreshPage(true)
    }
  }
  const handleExtendClick = async () => {
    await extendEstimate(idx ?? '', expiresAt)

    // refrescamos la pantalla
    if (!errorExtendEstimate) {
      setClickedIndex(-1)
      setRefreshPage(true)
    }
  }

  const handleBackToDeal = () => {
    if (fromDeal) {
      navigate(`/${AppPath.Deals}/view/${fromDeal}`)
    }
  }

  if (localError) return <div>Error: {localError}</div>

  if (errorEstimate) return <div>Error: {errorEstimate.message}</div>

  if (errorVoidEstimate) return <div>Error: {errorVoidEstimate.message}</div>

  if (errorExtendEstimate) return <div>Error: {errorExtendEstimate.message}</div>

  const names = {
    profileName: estimate?.profile_name ?? '',
    organizationName: estimate?.organization.name ?? '',
    clientName: estimate?.client.name ?? '',
  }
  const header = 'Al anular la cotización esta ya no estará activa y no podrá ser restaurada.'

  const buttonCaptions = ['Extender validez', 'Anular cotización']

  if (!estimate) return

  return (
    <Container size={useNewDocumentUI ? '4' : '3'}>
      {/* Conditional back button when coming from a deal */}
      {fromDeal && (
        <Box mb="4" className="print:hidden">
          <Flex>
            <Button variant="ghost" onClick={handleBackToDeal} className="flex items-center gap-2">
              <ArrowLeftIcon />
              Volver al Negocio
            </Button>
          </Flex>
        </Box>
      )}

      <PrintableCard>
        <EstimateDetail estimate={estimate} />
        <DocFooter names={names} />
      </PrintableCard>

      <Box my="4">
        <ButtonPrint />
      </Box>

      {!isAnulada && (
        <div className={`mx-auto shadow-md print:hidden ${useNewDocumentUI ? 'p-4 max-w-6xl' : 'p-2 max-w-3xl'}`}>
          <ButtonMenuSet
            captions={buttonCaptions}
            clickedIndex={clickedIndex}
            handleButtonClick={(n) => setClickedIndex(n)}
            short={true}
          />

          <div className="mt-8">
            {clickedIndex === 1 && (
              <DocAnula
                label="Motivo de anulación"
                header={header}
                name="motivo"
                value={motivo}
                docType={AppPath.Estimates}
                handleInputChange={handleInputChange}
                handleButtonClick={handleAnularClick}
              />
            )}
            {clickedIndex === 0 && (
              <>
                <div>Extiende la validez de la cotización ingresando una nueva fecha de expiración:</div>
                <div className="flex flex-col items-center">
                  <div className="max-w-40">
                    <InputField
                      label=""
                      name="expiresAt"
                      value={expiresAt}
                      type="date"
                      handleInputChange={handleInputChange}
                    />
                  </div>
                  <button
                    onClick={handleExtendClick}
                    className={`py-2 px-4 rounded mt-2 mb-2 transition-colors duration-200 ${
                      dateModified
                        ? 'bg-blue-500 text-white cursor-pointer'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    Extender
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {onClose && (
        <div className="flex justify-center print:hidden">
          <ButtonAction label="Volver al negocio" handleButtonClick={onClose!} />
        </div>
      )}
    </Container>
  )
}

export default EstimateDetailPage
